import { z } from 'zod'

import { Model } from '../../_kernel/architecture'

export type BoardCodes = (typeof ALLOWED_BOARD_CODES)[number]

const ALLOWED_BOARD_CODES = [
  'AI', // ALL INCLUSIVE
  'AS', // ALL INCLUSIVE SPECIAL
  'BH', // 1 BED AND BREAKFAST + 1 HALF BOARD
  'AB', // AMERICAN BREAKFAST
  'BB', // BED AND BREAKFAST
  'DB', // BUFFET BREAKFAST
  'GB', // ENGLISH BREAKFAST
  'IB', // IRISH BREAKFAST
  'SB', // SCOTTISH BREAKFAST
  'B1', // BREAKFAST FOR ONE GUEST
  'B2', // BREAKFAST FOR TWO GUESTS
  'CAB', // CARIBBEAN BREAKFAST
  'TFO', // 2-FOR-1 BUFFET
  'CB', // CONTINENTAL BREAKFAST
  'CE', // DINNER INCLUDED (BREAKFAST EXCLUDED)
  'FD', // FREE DINNER
  'FB', // FULL BOARD
  'FL+FD', // FREE LUNCH + FREE DINNER
  'FH', // 1 HALF BOARD + 1 FULL BOARD
  'FK', // FULL KITCHEN
  'HB', // HALF BOARD
  'BD', // BREAKFAST AND DINNER
  'BL', // BREAKFAST AND LUNCH
  'CO', // LUNCH INCLUDED (BREAKFAST EXCLUDED)
  'FLL', // FREE LUNCH
  'MB', // HALF BOARD WITH BEVERAGES INCLUDED
  'PB', // FULL BOARD BEVERAGES INCLUDED
  'RO', // ROOM ONLY
  'SC', // SELF CATERING
  'SAI', // SOFT ALL INCLUSIVE
] as const

const BREAKFAST_CODES = new Set([
  'AB',
  'BB',
  'CB',
  'DB',
  'GB',
  'IB',
  'SB',
  'B1',
  'B2',
  'CAB',
  'BH',
  'HB',
  'MB',
  'BD',
  'BL',
  'FB',
  'FH',
  'PB',
  'FL+FD',
  'AI',
  'AS',
  'SAI',
])

const BOARD_CODE_ICONS = {
  AB: 'IconBedAndBreakfast',
  AI: 'IconAllInclusive',
  AS: 'IconAllInclusive',
  BB: 'IconBedAndBreakfast',
  BH: 'IconLunch',
  CB: 'IconBedAndBreakfast',
  DB: 'IconBedAndBreakfast',
  FB: 'IconLunch',
  FH: 'IconLunch',
  GB: 'IconBedAndBreakfast',
  HB: 'IconLunch',
  IB: 'IconBedAndBreakfast',
  MB: 'IconLunchAndDrink',
  PB: 'IconLunchAndDrink',
  RO: 'IconRoomOnly',
  SB: 'IconBedAndBreakfast',
  SC: 'IconSelfCatering',
  B1: 'IconBedAndBreakfast',
  B2: 'IconBedAndBreakfast',
  BD: 'IconLunch',
  CAB: 'IconBedAndBreakfast',
  CE: 'IconLunch',
  CO: 'IconLunch',
  FD: 'IconLunch',
  FK: 'IconFullKitchen',
  'FL+FD': 'IconLunch',
  TFO: 'IconSelfCatering',
  FLL: 'IconLunch',
  SAI: 'IconAllInclusive',
  BL: 'IconLunch',
} as const

const ALL_INCLUSIVE_CODES = new Set(['AI', 'AS', 'SAI'])

export const BoardCodeValidation = z.object({
  value: z.enum(ALLOWED_BOARD_CODES, { required_error: '[Code] value is required' }),
})

export class BoardCode extends Model {
  static create({ value }: z.infer<typeof BoardCodeValidation>) {
    BoardCodeValidation.parse({ value })
    return new BoardCode(value)
  }

  static fromLiteral(value: string) {
    return BoardCode.create({ value: value as BoardCodes })
  }

  constructor(private readonly _value: BoardCodes) {
    super()
  }

  value(): BoardCodes { return this._value } // prettier-ignore

  hasBreakfast(): boolean {
    return BREAKFAST_CODES.has(this._value)
  }

  isAllInclusive(): boolean {
    return ALL_INCLUSIVE_CODES.has(this._value)
  }

  isRoomOnly(): boolean {
    return this._value === 'RO'
  }

  iconName() {
    const icon = BOARD_CODE_ICONS[this._value]
    if (!icon) return undefined

    return icon
  }

  toJSON() {
    return { value: this.value() }
  }
}
